import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import type { TeamKurz, TicketDetail, TicketKarte, TicketSpalte, Unternehmen } from "@kundeninfo/api-client";
import { Icon, schnellknopfDatum, istFreierTag, useBestaetigung, useSofortPopup } from "@kundeninfo/ui";
import type { Schnellknopf } from "@kundeninfo/ui";
import { Auswahl, Auswahlmenue, Chip, Eingabe, Steckbrief } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { anhangText, datumsMarke, heuteBeschriftung, alleFeiertage, berlinerTag } from "./ticketFormat.js";
import { Avatar, personOption } from "./Avatar.js";
import type { Schreibweg } from "./useSchreibweg.js";
import { ART } from "./TicketKarte.js";
import { TeamChip } from "./TeamChip.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { isOpen, rolesText } from "./hierarchy.js";
import { OwnerField } from "./OwnerField.js";
import { effortShare, hoursInput, parseHours, roundQuarter } from "./effort.js";
import { EFFORT_TEXT } from "./effortTexts.js";
import { ParentLine } from "./ParentLine.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DER STECKBRIEF DES TICKETDIALOGS (F41, Spec Teilaufgaben R20, Festlegung 3): Kopf mit Status-Chip und die Kennwerte
 * in der Folge der Spec — Teil von, Art, Unternehmen, Team, Eigentümer, Zuständig, Status, Wiedervorlage, Fällig bis,
 * Kunden, Ansprechpartner, Erstellt, Anhänge, Schätzung (Spec Aufwand A18; Stelle hinter „Anhänge“, siehe dort),
 * Beobachter. Die Kopffelder schreibt erst „Speichern“ im Fuß des Dialogs (`aendereKopf`); „Lösen“ und Beobachten sind
 * Sofort-Wege. Aus `TicketDialog.tsx` herausgelöst (Aufgabe 17), ohne Verhaltensänderung.
 *
 * Die Schnellknöpfe rücken die Wiedervorlage nur in der Oberfläche auf den Werktag (`kalenderHilfe`); PATCH speichert
 * das Datum wie gesendet, Wochenende/Feiertag ist nur ein Hinweis (Gesamtreview M4). Uhrzeit (F93) nur mit Datum: „keine“
 * und ein geleertes Datum leeren sie mit (die Oberfläche spiegelt die Domänenregel, kein 422-Umweg).
 */

/** `teamId` (Nachtrag 1e, F133): "" = allgemein. Ein Kopffeld wie die übrigen — geschrieben mit „Speichern".
 *  `owner` (Festlegung 9): `user:<id>`, `team:<id>` oder "" = System; `dueDate` "" = ohne (Spec Teilaufgaben R15).
 *  `estimate` (Spec Aufwand A18): Stundentext wie im Feld ("1500", "1,25"), "" = ohne Schätzung. */
export type Kopf = { title: string; descriptionMarkdown: string; assigneeUserId: string; columnId: string; resubmitDate: string;
                     resubmitTime: string; teamId: string; owner: string; dueDate: string; estimate: string };

/** Fixrunde 2 (Sichtprobe 1e): Team und Zuständig im Steckbrief mit fester Breite (me-auswahl/me-auswahlmenue
 *  `breite`, höchstens die Feldbreite) — der Wert kürzt sich, das Sofort-Popup nennt ihn voll. Ohne Breite wuchs die
 *  Teamauswahl mit „<Kürzel> <Name>“ über den Panelrand.
 *  Nachlauf 1e: 11 em statt 12 em. Die drei Wertspalten des Rasters (`auto 1fr` ×3) sind gleich breit; jedes Feld,
 *  das breiter ist als das breiteste übrige (die Statusauswahl, rund 168 px bei 15 px Schrift), macht das Panel um das
 *  DREIFACHE breiter — mit 12 em (180 px) öffnete es mit 928 statt 892 px. Mit 11 em (165 px) bestimmt wieder die
 *  Statusauswahl die Spalten (892 px, E2E tickets-panel), und Team/Zuständig stehen fast bündig darunter. */
const STECKBRIEF_AUSWAHL = "11em";

type Person = { id: string; name: string; email: string | null };

export type TicketSteckbriefProps = {
  d: TicketDetail; kopf: Kopf; companies: readonly Unternehmen[]; ichId: string; client: TicketsClient;
  spalten: readonly TicketSpalte[]; teams: readonly TeamKurz[];
  /** Personen für „Zuständig“ (mit dem gespeicherten Zuständigen) und die übrigen zum Eintragen als Beobachter. */
  leute: readonly Person[]; kandidaten: readonly Person[];
  gesperrt: boolean; feldFehler: Record<string, string>; feldRaeumen: (feld: string) => void;
  aendereKopf: (teil: Partial<Kopf>) => void;
  eingeklappt: boolean; onEingeklappt: (eingeklappt: boolean) => void;
  /** „Zum Elternticket ›“ (R23) mit der Kennung des Elterntickets. */
  onToParent: (parentId: string) => void; onDetached: (k: TicketKarte) => void; onError: (text: string) => void;
  /** „Anhänge“ im Steckbrief wechselt auf den Reiter. */
  onAnhaenge: () => void;
  /** Der Sofort-Weg des Beobachtens — seine Meldung zeigt der Dialog (Slot `meldungen`). */
  beobachten: Schreibweg<TicketDetail>;
};

export function TicketSteckbrief(p: TicketSteckbriefProps): ReactElement {
  const { d, kopf } = p;
  const feiertage = useMemo(() => alleFeiertage([d.kalender]), [d]);
  const beobachtenPopup = useSofortPopup<HTMLSpanElement>(rolesText(d.notifications));
  const [wahlZug, setWahlZug] = useState(0);
  const { bestaetigen } = useBestaetigung();
  /** Schätzung (A18, P6): `tippt` zwischen Eingabe und Verlassen — „Unlesbar“ erst danach, nicht schon bei „1,“; `gerundet`
   *  zeigt den Hinweis, solange der gerundete Wert unverändert steht. */
  const [tippt, setTippt] = useState(false);
  const [gerundet, setGerundet] = useState(false);

  /** Das Kreuz am Beobachter: eine ANDERE Person erst nach der Rückfrage (Entscheid 688), sich selbst ohne — das ist
   *  dasselbe wie „Nicht mehr beobachten“ und lässt sich sofort umkehren. */
  async function beobachterAustragen(w: { id: string; name: string }): Promise<void> {
    if (w.id !== p.ichId && !(await bestaetigen({ titel: HIERARCHY_TEXT.removeWatcherTitle(w.name),
      text: HIERARCHY_TEXT.removeWatcherText(w.name), bestaetigenText: HIERARCHY_TEXT.removeWatcher }))) return;
    await p.beobachten.schreiben(() => p.client.removeWatcher(d.id, w.id), "Der Beobachter ließ sich nicht austragen.");
  }

  const spalte = p.spalten.find((s) => s.id === d.columnId);
  const marke = datumsMarke(d.resubmitDate, d.heute, d.resubmitTime);
  const chipText = `${spalte?.name ?? "…"} · ${marke.text}`;
  const fehlerVon = (f: string): { fehler?: string } => {
    const text = p.feldFehler[f];
    return text === undefined ? {} : { fehler: text };
  };
  // Ohne Datum keine Uhrzeit (F93): „keine“ und ein geleertes Datum leeren sie mit.
  const setzeDatum = (datum: string) => p.aendereKopf(datum === "" ? { resubmitDate: "", resubmitTime: "" } : { resubmitDate: datum });
  const schnell = (k: Schnellknopf) =>
    setzeDatum(schnellknopfDatum(k, kopf.resubmitDate === "" ? null : kopf.resubmitDate, d.heute, feiertage) ?? "");
  // A18: Befund der Schätzung — vom Server oder lokal „unlesbar“ (erst nach dem Verlassen, nicht schon bei „1,“).
  const schaetzungFehler = p.feldFehler.estimatedMinutes
    ?? (!tippt && parseHours(kopf.estimate) === undefined ? EFFORT_TEXT.unreadable : undefined);
  // Ein Befund an einem Feld im Steckbrief klappt ihn auf, sonst stünde der Fehler unsichtbar da — auch der lokale der
  // Schätzung (Review G2).
  const kopfFehler = ["assigneeUserId", "columnId", "resubmitDate", "resubmitTime", "teamId", "ownerUserId", "ownerTeamId",
                      "dueDate"]
    .some((f) => p.feldFehler[f] !== undefined) || schaetzungFehler !== undefined;
  const anhaenge = anhangText(d.attachmentCounts);
  // F134: Kennwert „Team" nur mit wählbaren Teams oder bei privatem Ticket; das aktuelle Team steht immer zur Wahl.
  const waehlbareTeams = p.teams.filter((t) => t.active);
  const teamOptionen = d.team !== null && !waehlbareTeams.some((t) => t.id === d.team!.id)
    ? [{ id: d.team.id, shortCode: d.team.shortCode, name: d.team.name }, ...waehlbareTeams] : waehlbareTeams;
  const teamZeigen = waehlbareTeams.length > 0 || d.team !== null;
  const eltern = d.parent;
  const eigentuemerFehler = p.feldFehler.ownerUserId ?? p.feldFehler.ownerTeamId;
  // R9: „Übernehmen“ am Team-Ticket für Mitglieder dieses Teams, solange ich nicht schon zuständig bin.
  const uebernehmbar = d.owner.kind === "TEAM" && p.teams.some((t) => t.id === d.owner.team?.id && t.mitglied)
    && kopf.assigneeUserId !== p.ichId;
  const meldet = d.notifications.roles.length > 0 && !d.notifications.muted;
  const { gesperrt, beobachten } = p;
  const eigen = effortShare(d.estimatedMinutes, d.workedMinutes);
  /** Rundet beim Verlassen auf Viertelstunden (P6) — nur nach einer Eingabe: Ein bloßes Verlassen ändert den Kopf nie,
   *  auch nicht bei einer gespeicherten Schätzung außerhalb des Rasters. */
  const rundeSchaetzung = () => {
    if (!tippt) return;
    setTippt(false);
    const m = parseHours(kopf.estimate);
    if (typeof m !== "number") return;
    const r = roundQuarter(m);
    setGerundet(r !== m);
    if (hoursInput(r) !== kopf.estimate) p.aendereKopf({ estimate: hoursInput(r) });
  };

  return (
    <Steckbrief art="dialog" className="td__steckbrief" titel={`${d.ticketNumber} ${d.title}`}
      eingeklappt={p.eingeklappt && !kopfFehler} onEingeklappt={(e) => p.onEingeklappt(e.eingeklappt)}
      icon={<Icon name={ART[d.kind].symbol} label={ART[d.kind].name} />}
      status={marke.art === null ? <Chip>{chipText}</Chip> : <Chip statusArt={marke.art}>{chipText}</Chip>}
      werte={(
        <dl>
          {eltern !== null && (
            <>
              <dt>{HIERARCHY_TEXT.partOf}</dt>
              <dd data-frei data-zeile="voll" className="td__frei">
                {/* R23, K8: der Panelknopf des Elterntickets über `abbrechen()`, wenn der Dialog darunter das Elternticket ist
                    (Ungespeichertes fragt nach), sonst seitlich; „Lösen“ schreibt sofort (eigener Weg, Festlegung 20). */}
                <ParentLine parent={eltern} child={{ id: d.id, displayNumber: d.displayNumber, version: d.version }} client={p.client}
                  disabled={gesperrt} onToParent={() => p.onToParent(eltern.id)} onDetached={p.onDetached} onError={p.onError} />
              </dd>
            </>
          )}
          <dt>Art</dt>
          <dd className="td__art"><Icon name={ART[d.kind].symbol} label="" /> {ART[d.kind].name}</dd>
          {/* F121: Was Nutzer frei befüllen, zählt nicht zur Panelbreite und bricht um (`td__frei`, T-11). */}
          <dt>Unternehmen</dt>
          <dd data-frei className="td__frei">
            <Chip {...hausfarbeChip(p.companies, d.companyShortCode)}>{d.companyShortCode}</Chip> {d.companyName}
            {" "}<TeamChip team={d.team} />
          </dd>
          {teamZeigen && (
            <>
              <dt>Team</dt>
              {/* E440: Das Team einer Teilaufgabe folgt dem Elternticket — lesend. Der Teamname ist Nutzertext: Die Zelle zählt
                  nicht zur Panelbreite und bricht um (`td__frei`, F121, T-11) — sonst öffnete das Kind-Panel mit 960 statt 897 px. */}
              {eltern !== null ? (
                <dd data-frei className="td__frei">
                  {d.team === null ? "Allgemein" : `${d.team.shortCode} ${d.team.name}`} · {HIERARCHY_TEXT.teamLikeParent}
                </dd>
              ) : (
                <dd data-frei>
                  <Auswahl kompakt breite={STECKBRIEF_AUSWAHL} beschriftung="Team" wert={kopf.teamId} gesperrt={gesperrt}
                    {...fehlerVon("teamId")}
                    onGeaendert={(e) => { p.feldRaeumen("teamId"); p.aendereKopf({ teamId: e.wert }); }}>
                    <option value="">Allgemein</option>
                    {teamOptionen.map((t) => <option key={t.id} value={t.id}>{`${t.shortCode} ${t.name}`}</option>)}
                  </Auswahl>
                </dd>
              )}
            </>
          )}
          <dt>{HIERARCHY_TEXT.owner}</dt>
          <dd data-frei className="td__eigentuemer">
            <OwnerField client={p.client} ticketId={d.id} wert={kopf.owner} aktuell={d.owner} ichId={p.ichId}
              breite={STECKBRIEF_AUSWAHL} gesperrt={gesperrt} {...(eigentuemerFehler === undefined ? {} : { fehler: eigentuemerFehler })}
              onGeaendert={(w) => {
                p.feldRaeumen("ownerUserId");
                p.feldRaeumen("ownerTeamId");
                p.aendereKopf({ owner: w });
              }} />
          </dd>
          <dt>Zuständig</dt>
          <dd data-frei className="td__zustaendig">
            {/* me-auswahlmenue (Entscheid 53): der Auslöser zeigt den Avatar der gewählten
                Person; die eigene Person steht als „ich“ oben (Rand in der Aktionsfarbe). */}
            <Auswahlmenue kompakt breite={STECKBRIEF_AUSWAHL} beschriftung="Zuständig" wert={kopf.assigneeUserId} gesperrt={gesperrt}
              {...fehlerVon("assigneeUserId")}
              onGeaendert={(e) => { p.feldRaeumen("assigneeUserId"); p.aendereKopf({ assigneeUserId: e.wert }); }}>
              <option value="" data-art="niemand">Niemand</option>
              {p.leute.map((u) => (
                <option key={u.id} value={u.id} {...(u.id === p.ichId ? { "data-art": "ich" } : {})}
                  {...personOption(u)}>{u.name}</option>
              ))}
            </Auswahlmenue>
            {/* R9, E443: „Übernehmen“ setzt nur Zuständig im Formular — gespeichert wird mit „Speichern“, ohne Hinweis. */}
            {uebernehmbar && (
              <KiKnopf art="leise" kompakt deaktiviert={gesperrt} onClick={() => p.aendereKopf({ assigneeUserId: p.ichId })}>
                {HIERARCHY_TEXT.takeOver}
              </KiKnopf>
            )}
          </dd>
          <dt>Status</dt>
          <dd data-frei>
            <Auswahl kompakt beschriftung="Status" wert={kopf.columnId} gesperrt={gesperrt}
              {...fehlerVon("columnId")}
              onGeaendert={(e) => { p.feldRaeumen("columnId"); p.aendereKopf({ columnId: e.wert }); }}>
              {p.spalten.length === 0 && <option value={d.columnId}>…</option>}
              {p.spalten.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Auswahl>
          </dd>
          {/* Eine Beschriftung (der Kennwert), das Feld trägt den Namen nur für die Vorlesestimme;
              die Schnellknöpfe in einer Zeile darunter (Sichtprobe 8); die Zeile spannt über die
              volle Breite (`data-zeile="voll"`, me-design 7409714, F127). */}
          <dt>Wiedervorlage</dt>
          <dd data-frei data-zeile="voll" className="td__wiedervorlage">
            <span className="td__datumzeit">
            <Eingabe art="datum" aria-label="Wiedervorlage" className="td__datum" id="td-wiedervorlage" name="td-wiedervorlage"
              wert={kopf.resubmitDate} {...(gesperrt ? { gesperrt: true } : {})}
              {...(p.feldFehler.resubmitDate !== undefined ? { fehler: p.feldFehler.resubmitDate } : {})}
              onGeaendert={(e) => { p.feldRaeumen("resubmitDate"); setzeDatum(e.wert); }} />
            {/* Uhrzeit (F93, F125): me-eingabe art="uhrzeit" — Zifferntastatur, rechtsbündig,
                normalisiert beim Verlassen auf „HH:MM“; ohne Datum gesperrt (F93). */}
            <Eingabe art="uhrzeit" aria-label="Uhrzeit" className="td__uhrzeit" id="td-uhrzeit" name="td-uhrzeit"
              wert={kopf.resubmitTime}
              {...(gesperrt || kopf.resubmitDate === "" ? { gesperrt: true } : {})}
              {...(p.feldFehler.resubmitTime !== undefined ? { fehler: p.feldFehler.resubmitTime } : {})}
              onGeaendert={(e) => { p.feldRaeumen("resubmitTime"); p.aendereKopf({ resubmitTime: e.wert }); }} />
            </span>
            <span className="td__schnell">
              <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("TAG")}>+1 Tag</KiKnopf>
              <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("WOCHE")}>+1 Woche</KiKnopf>
              <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("MONAT")}>+1 Monat</KiKnopf>
              <KiKnopf art="leise" className="td__schnellknopf" deaktiviert={gesperrt} onClick={() => schnell("KEINE")}>keine</KiKnopf>
            </span>
            <span className="td__datumhinweis">
              {kopf.resubmitDate === "" ? "ohne Wiedervorlage: steht oben in „Jetzt“" : (
                <>
                  <span>{heuteBeschriftung(kopf.resubmitDate)}</span>
                  {istFreierTag(kopf.resubmitDate, feiertage) ? " · Wochenende/Feiertag nur Hinweis" : ""}
                </>
              )}
            </span>
          </dd>
          {/* R15: „Fällig bis“ unabhängig von der Wiedervorlage (E436); offen und überschritten mit Kennzeichen. */}
          <dt>{HIERARCHY_TEXT.dueDate}</dt>
          <dd data-frei className="td__faellig">
            <Eingabe art="datum" aria-label={HIERARCHY_TEXT.dueDate} className="td__datum" id="td-faellig" name="td-faellig"
              wert={kopf.dueDate} {...(gesperrt ? { gesperrt: true } : {})} {...fehlerVon("dueDate")}
              onGeaendert={(e) => { p.feldRaeumen("dueDate"); p.aendereKopf({ dueDate: e.wert }); }} />
            {isOpen(d.stateKind) && d.dueDate !== null && d.dueDate < d.heute && <Chip statusArt="fehler">{HIERARCHY_TEXT.overdue}</Chip>}
          </dd>
          <dt>Kunden</dt>
          <dd data-frei className="td__frei">
            {d.customers.length === 0 ? "ohne Kunde (intern)" : d.customers.map((c) => c.name).join(", ")}
          </dd>
          <dt>Ansprechpartner</dt>
          <dd data-frei className="td__frei">
            {d.teile.contacts.length === 0 ? "–" : d.teile.contacts.map((c) => c.name).join(", ")}
          </dd>
          <dt>Erstellt</dt>
          <dd data-frei className="td__frei">{`${d.createdBy?.name ?? "–"}, ${heuteBeschriftung(berlinerTag(d.createdAt))}`}</dd>
          <dt>Anhänge</dt>
          <dd data-frei>
            {anhaenge === null ? "keine" : (
              <KiKnopf art="leise" className="td__anhangzahl" onClick={p.onAnhaenge}>{anhaenge}</KiKnopf>
            )}
          </dd>
          {/* A18, P5: Feld 6 em und Kennzeichen „Ist …“ (eigene Werte) zählen nicht zur Panelbreite (`td__frei`); das
              Kennzeichen bricht bei Platzmangel unter das Feld. Gespeichert mit „Speichern“ im Fuß.
              Stelle (E2E-Nacharbeit, Öffnungsbreite 897 px): im freien dritten Paar hinter „Anhänge“, nicht hinter „Fällig bis“.
              Das Raster hat drei Paare je Zeile, jede Beschriftungsspalte ist so breit wie ihre längste Beschriftung, die
              Wertspalten gleich breit (`1fr`). Hinter „Fällig bis“ schöbe die Schätzung „Ansprechpartner“ in die erste Spalte
              und „Anhänge“ in die dritte — die Summe der Beschriftungsspalten und damit das Blatt änderte sich (859 statt 897 px).
              Hier füllt sie die Lücke der letzten Zeile, mit und ohne Zeile „Team“, und alle übrigen Paare bleiben, wo sie waren. */}
          <dt>{EFFORT_TEXT.estimate}</dt>
          <dd data-frei className="td__frei td__schaetzung">
            <span className="td__schaetzungsfeld" onBlur={rundeSchaetzung}>
              <Eingabe art="dezimal" einheit="h" aria-label={EFFORT_TEXT.estimateName} id="td-schaetzung" name="td-schaetzung"
                wert={kopf.estimate} {...(gesperrt ? { gesperrt: true } : {})}
                {...(schaetzungFehler === undefined ? {} : { fehler: schaetzungFehler })}
                {...(gerundet ? { hilfe: EFFORT_TEXT.rounded } : {})}
                onGeaendert={(e) => {
                  p.feldRaeumen("estimatedMinutes");
                  setTippt(true);
                  setGerundet(false);
                  p.aendereKopf({ estimate: e.wert });
                }} />
            </span>
            {d.workedMinutes > 0 && (
              <Chip {...(eigen !== null && eigen.overrun > 0 ? { statusArt: "fehler" as const } : {})}>
                {EFFORT_TEXT.workedChip(d.workedMinutes, eigen?.percent ?? null)}
              </Chip>
            )}
          </dd>
          {/* F122: eine eigene Zeile über die volle Breite des Kennwert-Rasters (me-design 7409714). */}
          <dt>Beobachter</dt>
          <dd data-frei data-zeile="voll" className="td__beobachter td__frei">
            {/* me-avatar mit Namen je Beobachter (Entscheid 184), das Kreuz trägt aus. */}
            {d.watchers.length === 0 ? <span className="td__niemand">niemand</span> : d.watchers.map((w) => (
              <span key={w.id} className="td__beobachterin">
                <Avatar person={w} ich={w.id === p.ichId} />
                <KiKnopf art="leise" className="td__austragen" name={`${w.name} als Beobachter austragen`}
                  deaktiviert={beobachten.laeuft}
                  onClick={() => { void beobachterAustragen(w); }}>
                  <Icon name="xmark" size={12} />
                </KiKnopf>
              </span>
            ))}
            <span className="td__beobachten">
              {/* R34: „Nicht mehr beobachten“, solange eine Rolle meldet — schaltet stumm; das Popup nennt die Rollen. */}
              <span ref={beobachtenPopup}>
                <KiKnopf art="leise" deaktiviert={beobachten.laeuft}
                  onClick={() => { void beobachten.schreiben(() => (meldet ? p.client.unwatchTicket(d.id)
                    : p.client.watchTicket(d.id)), "Das Beobachten ließ sich nicht ändern."); }}>
                  <Icon name={meldet ? "eye-slash" : "eye"} size={14} className="td__beobachten-symbol" />
                  {meldet ? HIERARCHY_TEXT.unwatch : HIERARCHY_TEXT.watch}
                </KiKnopf>
              </span>
              {p.kandidaten.length > 0 && (
                // Ohne `wert` (nichts gewählt, der Auslöser zeigt den Platzhalter); nach jeder
                // Wahl neu gezeichnet, damit er wieder leer ist — auch wenn das Eintragen scheitert.
                <Auswahlmenue key={`beobachter-${wahlZug}`} kompakt beschriftung="Beobachter hinzufügen"
                  platzhalter="Beobachter hinzufügen" gesperrt={beobachten.laeuft}
                  onGeaendert={(e) => {
                    if (e.wert === "") return;
                    setWahlZug((n) => n + 1);
                    void beobachten.schreiben(() => p.client.addWatcher(d.id, e.wert),
                      "Der Beobachter ließ sich nicht eintragen.");
                  }}>
                  {p.kandidaten.map((u) => <option key={u.id} value={u.id} {...personOption(u)}>{u.name}</option>)}
                </Auswahlmenue>
              )}
            </span>
          </dd>
        </dl>
      )} />
  );
}
